<template>
  <div>
    <p v-if="statusMessage" class="text-body-3 mb-16">{{ statusMessage }}</p>
    <ul class="flex flex-col gap-16">
      <li v-for="cert in visibleCertifications" :key="cert.key" class="flex gap-16 items-start">
        <component :is="cert.icon" class="flex-shrink-0" :width="40" :height="40" :active="true" />
        <div class="flex-1 min-w-0">
          <p class="text-strong-body">{{ cert.title }}</p>
          <p class="text-body-3">{{ cert.detailsLine }}</p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { format, isValid, parseISO } from 'date-fns';
import CPRBadge from '@/graphics/badges/CPRBadge.vue';
import FirstAidBadge from '@/graphics/badges/FirstAidBadge.vue';
import { markComponentRaw } from '@/helpers/markComponentRaw';
import { CERTIFICATION_ORGANIZATION_OPTIONS } from '../../edit/__types__/CareTypeFormOptions';

interface Badge {
  completedAt?: Date | string | null;
  expiresAt?: Date | string | null;
  name: string;
  status: string;
}

interface ProviderCertification {
  type?: string;
  certificationType?: string;
  certification_type?: string;
  issuingOrganization?: string | null;
  issuing_organization?: string | null;
  issuedOn?: Date | string | null;
  issued_on?: Date | string | null;
  expiresOn?: Date | string | null;
  expires_on?: Date | string | null;
}

const CERTIFICATION_ITEMS = [
  {
    badgeName: 'cpr_certified',
    certType: 'CPR',
    key: 'cpr',
    icon: markComponentRaw(CPRBadge),
    title: 'CPR certification',
  },
  {
    badgeName: 'first_aid_training',
    certType: 'FIRST_AID',
    key: 'firstAid',
    icon: markComponentRaw(FirstAidBadge),
    title: 'First aid certification',
  },
];

const asList = <T,>(value: T[] | undefined | null): T[] => (Array.isArray(value) ? value.filter(Boolean) : []);

const canonicalCertType = (value?: unknown): string => {
  if (value == null) return '';
  const raw =
    typeof value === 'object' && value !== null && 'name' in value
      ? String((value as { name?: string }).name || '')
      : String(value);
  const normalized = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  if (normalized === 'CPR' || normalized === 'CPR_CERTIFIED') return 'CPR';
  if (normalized === 'FIRST_AID' || normalized === 'FIRST_AID_TRAINING') return 'FIRST_AID';
  return normalized;
};

const readCertification = (entry?: ProviderCertification | null) => {
  if (!entry) return null;

  return {
    type: canonicalCertType(entry.type ?? entry.certificationType ?? entry.certification_type),
    issuingOrganization: entry.issuingOrganization ?? entry.issuing_organization ?? null,
    issuedOn: entry.issuedOn ?? entry.issued_on ?? null,
    expiresOn: entry.expiresOn ?? entry.expires_on ?? null,
  };
};

export default {
  name: 'ProfileCertifications',
  props: {
    badges: {
      type: Array as () => Badge[],
      default: () => [],
    },
    certifications: {
      type: Array as () => ProviderCertification[],
      default: () => [],
    },
    hasCertifications: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    statusMessage(): string {
      return this.hasCertifications ? 'These are self-reported, not verified' : '';
    },
    visibleCertifications() {
      const certifications = asList(this.certifications).map(readCertification);
      const badges = asList(this.badges);

      return CERTIFICATION_ITEMS.map((item) => {
        const certification = certifications.find((entry) => entry?.type === item.certType);
        const badge = badges.find((entry) => entry.name === item.badgeName);
        const isComplete = badges.some((entry) => entry.name === item.badgeName && entry.status === 'COMPLETE');
        const organization = isComplete ? this.organizationLabel(certification?.issuingOrganization) : '';
        const issued = isComplete ? this.formatMonthYear(certification?.issuedOn) : '';
        const expires = isComplete ? this.formatMonthYear(certification?.expiresOn || badge?.expiresAt) : '';
        const details: string[] = [];

        if (organization) details.push(organization);
        if (issued) details.push(`Issued ${issued}`);
        if (expires) details.push(`Expires ${expires}`);
        else if (organization || issued) details.push('Does not expire');

        return {
          ...item,
          isComplete,
          detailsLine: details.join(' · ') || 'No details added',
        };
      }).filter((item) => item.isComplete);
    },
  },
  methods: {
    organizationLabel(value?: string | null): string {
      if (!value) return '';
      const match = CERTIFICATION_ORGANIZATION_OPTIONS.find((option) => option.value === value || option.display === value);
      return match?.display || value;
    },
    parseDate(value?: Date | string | null): Date | null {
      if (!value) return null;

      if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        }
        const parsed = parseISO(value);
        return isValid(parsed) ? parsed : null;
      }

      const date = new Date(value);
      return isValid(date) ? date : null;
    },
    formatMonthYear(value?: Date | string | null): string {
      const date = this.parseDate(value);
      return date ? format(date, 'MMM yyyy') : '';
    },
  },
};
</script>
